import { useId, useMemo } from "react";
import type { RdaVocabularyEntry } from "@/api/types";

interface RdaTypePickerProps {
  /** Visible name of the element, e.g. "Type de contenu". */
  label: string;
  /** UNIMARC field carrying the element, shown as a hint for cataloguers. */
  unimarcTag: string;
  vocabulary: RdaVocabularyEntry[];
  values: string[];
  onChange: (values: string[]) => void;
}

const collator = new Intl.Collator("fr", { sensitivity: "base" });

/**
 * Edits a repeatable RDA-FR element (content, media or carrier type).
 *
 * Selected values are shown as removable chips; new values are added from a
 * native select, which keeps keyboard and screen reader support for long
 * vocabularies (56 carrier types).
 */
export function RdaTypePicker({
  label,
  unimarcTag,
  vocabulary,
  values,
  onChange,
}: RdaTypePickerProps) {
  const selectId = useId();

  const labelByCode = useMemo(
    () => new Map(vocabulary.map((entry) => [entry.code, entry.label])),
    [vocabulary]
  );

  const available = useMemo(
    () =>
      vocabulary
        .filter((entry) => !values.includes(entry.code))
        .sort((a, b) => collator.compare(a.label, b.label)),
    [vocabulary, values]
  );

  return (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 flex items-baseline gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        <span className="font-mono text-xs font-normal text-gray-500 dark:text-gray-400">
          {unimarcTag}
        </span>
      </legend>

      {values.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-2">
          {values.map((code) => {
            // Codes imported from a newer vocabulary may be unknown here: show the raw code.
            const entryLabel = labelByCode.get(code) ?? code;
            return (
              <li
                key={code}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 py-1 pr-1 pl-3 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-100"
              >
                <span>{entryLabel}</span>
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{code}</span>
                <button
                  type="button"
                  onClick={() => {
                    onChange(values.filter((value) => value !== code));
                  }}
                  aria-label={`Retirer ${entryLabel}`}
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">Non renseigné</p>
      )}

      <label htmlFor={selectId} className="sr-only">
        Ajouter : {label.toLowerCase()}
      </label>
      <select
        id={selectId}
        value=""
        disabled={available.length === 0}
        onChange={(e) => {
          if (e.target.value !== "") {
            onChange([...values, e.target.value]);
          }
        }}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
      >
        <option value="">Ajouter…</option>
        {available.map((entry) => (
          <option key={entry.code} value={entry.code}>
            {entry.label} ({entry.code})
          </option>
        ))}
      </select>
    </fieldset>
  );
}
